import { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Trash2, Plus, CheckCircle, Clock } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import api from "@/utils/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const fileSchema = z
  .object({
    type: z.string().optional(),
    file: z.any(),
  })
  .refine(
    (data) => {
      if (data.type && data.type.trim() !== "") {
        return data.file instanceof File;
      }
      return true;
    },
    {
      message: "File is required when file type is selected",
      path: ["file"],
    }
  );

const tabSchema = (tabsOrder) =>
  z.object({
    tab_name: z.enum(tabsOrder.map((t) => t.id)),
    status: z.number().int().min(0).max(1),
  });

const isValidFile = (file) =>
  file instanceof File || (typeof file === "string" && file.length > 0);

const remarksSchema = (tabsOrder, loom_details, after_purchase) =>
  z.object({
    remarks: z.string().min(1, "Remarks are required"),
    remarks_date: z.string().min(1, "Remarks date is required"),
    geo_tag: z.any().refine(isValidFile, "Geo tag is required"),
    files: z.array(fileSchema).optional(),
    tabs: z
      .array(tabSchema(tabsOrder))
      .length(tabsOrder.length, `Must have exactly ${tabsOrder.length} tabs`)
      .refine((tabs) => tabs.every((tab) => tab.status === 1), {
        message: "All tabs must be checked",
      }),
    loom_id:
      !after_purchase && loom_details && loom_details.length > 0
        ? z.array(z.number()).min(1, "Please select at least one loom")
        : z.array(z.number()).optional(),
  });

const FieldInspectionForm = ({
  formType,
  form_id,
  after_purchase = false,
  setData,
  loom_details = [],
  uploads_array = [],
  application,
  tabsOrder,
  role,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [tabChecklist, setTabChecklist] = useState([]);
  const [selectedLooms, setSelectedLooms] = useState([]);

  const {
    control,
    handleSubmit,
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(
      remarksSchema(tabsOrder, loom_details, after_purchase)
    ),
    defaultValues: {
      remarks: "",
      remarks_date: new Date().toISOString().split("T")[0],
      geo_tag: undefined,
      files: [{ type: "", file: null }],
      tabs: [],
      loom_id: [],
    },
  });

  useEffect(() => {
    if (application && tabsOrder) {
      const initialTabs = tabsOrder.map((tab) => ({
        tab_name: tab.id,
        status: 0,
      }));

      setTabChecklist(initialTabs);
      setValue("tabs", initialTabs, { shouldValidate: false });
    }
  }, [application, tabsOrder, setValue]);

  const cardClassName = role === "district_admin" ? "mb-4 bg-white" : "mb-4";

  const { fields, append, remove } = useFieldArray({
    control,
    name: "files",
  });

  const handleTabCheckboxChange = (tabId, isChecked) => {
    const updatedTabs = tabChecklist.map((tab) =>
      tab.tab_name === tabId
        ? {
            ...tab,
            status: isChecked ? 1 : 0,
          }
        : tab
    );
    setTabChecklist(updatedTabs);
    setValue("tabs", updatedTabs, { shouldValidate: true });
    trigger("tabs");
  };

  const handleLoomSelection = (loomId) => {
    setSelectedLooms((prev) => {
      const newSelection = prev.includes(loomId)
        ? prev.filter((id) => id !== loomId)
        : [...prev, loomId];

      setValue("loom_id", newSelection, { shouldValidate: true });
      return newSelection;
    });
  };

  const handleFileChange = (index, file) => {
    if (file) {
      setValue(`files.${index}.file`, file, { shouldValidate: true });
    }
  };

  const handleTypeChange = (index, value) => {
    setValue(`files.${index}.type`, value, { shouldValidate: true });
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);

      const submitData = new FormData();
      submitData.append("remarks", formData.remarks);
      submitData.append("remarks_date", formData.remarks_date);
      submitData.append("id", form_id);
      if (formData.geo_tag instanceof File) {
        submitData.append("geo_tag", formData.geo_tag);
      }
      submitData.append("tabs", JSON.stringify(formData.tabs));

      if (formData.loom_id && formData.loom_id.length > 0) {
        formData.loom_id.forEach((item, i) => {
          submitData.append(`loom_id[${i}]`, item);
        });
      }

      formData.files.forEach((item, i) => {
        if (item.type && item.type.trim() !== "") {
          submitData.append(`files[${i}][type]`, item.type);
          if (item.file instanceof File) {
            submitData.append(`files[${i}][file]`, item.file);
          }
        }
      });

      const url = after_purchase
        ? `${formType}/after_purchase_field_visit`
        : `${formType}/field_visit`;

      const response = await api.post(url, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setData(response?.data?.data);

      toast({
        title: "Saved",
        description: "Field inspection submitted successfully.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit field inspection",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
      setConfirmationModalOpen(false);
    }
  };

  const completedCount = tabChecklist.filter((tab) => tab.status === 1).length;
  const hasTabError = errors.tabs && completedCount === 0;

  const handleConfirmation = async () => {
    const isValid = await trigger();
    if (isValid) {
      setConfirmationModalOpen(true);
    }
  };

  return (
    <div className="space-y-6">
      {!after_purchase && loom_details && loom_details.length > 0 && (
        <Card className="bg-white mt-5">
          <CardHeader>
            <CardTitle>
              Select Looms for Provisional Sanction{" "}
              <span className="text-red-500">*</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
              {loom_details.map((loom) => {
                const isSelected = selectedLooms.includes(loom.id);
                return (
                  <div
                    key={loom.id}
                    onClick={() => handleLoomSelection(loom.id)}
                    className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected
                        ? "border-blue-500 bg-blue-50 shadow-md"
                        : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-gray-900">
                          {loom.loom_name}
                        </h3>
                        {loom.serial_number && (
                          <p className="text-sm text-gray-600 mt-1">
                            Serial: {loom.serial_number}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          Purchase Date:{" "}
                          {new Date(loom.purchase_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? "bg-blue-500 border-blue-500"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>

                    {loom.attachments && loom.attachments.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          Attachments ({loom.attachments.length}):
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {loom.attachments.map((attachment, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                            >
                              {attachment.attachment_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Selected:</span>{" "}
                {selectedLooms.length} of {loom_details.length} looms
              </p>
            </div>

            {errors.loom_id && (
              <p className="text-sm text-red-500 mt-2">
                {errors.loom_id.message}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab Checklist Section */}
      <Card className={cardClassName} style={{ backgroundColor: "#fff" }}>
        <CardHeader>
          <CardTitle>Tab Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 mt-4">
            <div className="space-y-3">
              {tabsOrder?.map((tab) => {
                const tabStatus = tabChecklist.find(
                  (t) => t.tab_name === tab.id
                );
                const isCompleted = tabStatus?.status === 1;

                return (
                  <div
                    key={tab.id}
                    className={`flex items-center justify-between p-3 border rounded-lg ${
                      isCompleted
                        ? "bg-green-50 border-green-200"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {isCompleted ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <Clock className="h-5 w-5 text-gray-400" />
                      )}
                      <span
                        className={`font-medium ${
                          isCompleted ? "text-green-800" : "text-gray-600"
                        }`}
                      >
                        {tab.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isCompleted}
                        onChange={(e) =>
                          handleTabCheckboxChange(tab.id, e.target.checked)
                        }
                        className="h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              className={`mt-4 p-3 border rounded-lg ${
                hasTabError
                  ? "bg-red-50 border-red-200"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`font-medium ${
                    hasTabError ? "text-red-800" : "text-blue-800"
                  }`}
                >
                  Overall Completion
                </span>
                <span
                  className={`font-bold ${
                    hasTabError ? "text-red-800" : "text-blue-800"
                  }`}
                >
                  {completedCount} of {tabsOrder?.length || 0} tabs completed
                </span>
              </div>
            </div>
          </div>
          {errors.tabs && (
            <p className="text-sm text-red-500 mt-2">{errors.tabs.message}</p>
          )}
        </CardContent>
      </Card>

      {/* Remarks Date Section */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle>Field Inspection Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="remarks_date">
                Field Inspection Date <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="remarks_date"
                  type="date"
                  {...register("remarks_date")}
                  className={errors.remarks_date ? "border-red-500" : ""}
                />
              </div>
              {errors.remarks_date && (
                <p className="text-sm text-red-500">
                  {errors.remarks_date.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="geo_tag" required>
                GPS Location
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="geo_tag"
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setValue("geo_tag", file, { shouldValidate: true });
                    }
                  }}
                  className={errors.geo_tag ? "border-red-500" : ""}
                />
                {errors.geo_tag && (
                  <p className="text-sm text-red-500">
                    {errors.geo_tag.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PNG, JPG, JPEG Files max size 2MB
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="remarks">
              Remarks <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="remarks"
              rows={3}
              {...register("remarks")}
              placeholder="Enter your remarks..."
              className={errors.remarks ? "border-red-500" : ""}
            />
            {errors.remarks && (
              <p className="text-sm text-red-500">{errors.remarks.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white">
        <CardHeader>
          <CardTitle>Upload Files</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 mt-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="flex items-start md:flex-row gap-4 border p-4 rounded-lg"
            >
              <div className="flex-1">
                <Label>File Type</Label>
                <Select
                  onValueChange={(value) => handleTypeChange(index, value)}
                  defaultValue={field.type || ""}
                >
                  <SelectTrigger
                    className={
                      errors.files?.[index]?.type ? "border-red-500" : ""
                    }
                  >
                    <SelectValue placeholder="Select File Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {uploads_array.length > 0 ? (
                      uploads_array.map((item, i) => (
                        <SelectItem key={i} value={item.value}>
                          {item.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem disabled value="none">
                        No file types available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {errors.files?.[index]?.type && (
                  <p className="text-sm text-red-500">
                    {errors.files[index].type.message}
                  </p>
                )}
              </div>

              <div className="flex-1">
                <Label>Upload File</Label>
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange(index, e.target.files[0])}
                  className={
                    errors.files?.[index]?.file ? "border-red-500" : ""
                  }
                />
                {errors.files?.[index]?.file && (
                  <p className="text-sm text-red-500">
                    {errors.files[index].file.message}
                  </p>
                )}
              </div>

              <Button
                type="button"
                variant="destructive"
                onClick={() => remove(index)}
                className="self-end"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={() => append({ type: "", file: null })}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add More
          </Button>

          {errors.files && typeof errors.files.message === "string" && (
            <p className="text-sm text-red-500">{errors.files.message}</p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="submit"
          className="min-w-[150px]"
          onClick={handleConfirmation}
          disabled={isSubmitting}
        >
          Submit Field Inspection
        </Button>
      </div>
      <Dialog
        open={confirmationModalOpen}
        onOpenChange={setConfirmationModalOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Field Inspection Confirmation</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <div className="py-4">
              Are you sure you want to Submit this Application. Once done the
              Application will be ready for Provisional Sanction.
            </div>
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setConfirmationModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                "Submit"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldInspectionForm;
